import {
	Avatar,
	Box,
	Breadcrumbs,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	styled,
	ThemeProvider,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import CssBaseline from '@mui/material/CssBaseline'
import { darkTheme, lightTheme } from 'constants/defaultValues'
import {
	getSettings,
	getUserDeviceInfo,
	removeCurrentUser,
	setSettings,
} from 'helpers/storage'
import { selectGnbToggle, setGnbToggle } from 'hooks/gnbToggle'
import { selectDisplayMode, setDisplayMode } from 'hooks/themeMode'
import { removeUserInfo, selectuserInfo } from 'hooks/userInfo'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import React from 'react'
import Logout from '@mui/icons-material/Logout'
import {
	apiDashboardStats,
	apiGetMenuList,
	apiLogout,
	apiPopupList,
} from 'api/api'
import { useQuery } from '@tanstack/react-query'
import Gnb from 'components/Gnb'
import { DrawerHeader, MaterialUISwitch } from 'components/ApplicationStyles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MuiDrawer from '@mui/material/Drawer'
import MenuIcon from '@mui/icons-material/Menu'
import { confirmAlert } from 'util/util'
import { DeviceInfoModel, SSEEventType } from 'constants/types'
import NoticePopup from 'components/NoticePopup'
import { selectEnvSettingInfo, setEnvSetting } from 'hooks/envSettings'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import HomeIcon from '@mui/icons-material/Home'

interface Props {
	/**
	 * Injected by the documentation to work in an iframe.
	 * You won't need it on your project.
	 */
	window?: () => Window
	children: React.ReactElement
}
interface AppBarProps extends MuiAppBarProps {
	open?: boolean
}

const drawerWidth = 240

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
	open?: boolean
}>(({ theme, open }) => ({
	flexGrow: 1,
	padding: theme.spacing(3),
	background:
		theme.palette.mode === 'dark'
			? 'linear-gradient(135deg, #0f0f23 0%, #1a1a40 50%, #2d2d5f 100%)'
			: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 50%, #dde7ff 100%)',
	height: 'calc(100vh - 65px)',
	position: 'relative',
	'&::before': {
		content: '""',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		background:
			theme.palette.mode === 'dark'
				? `
				radial-gradient(circle at 25% 25%, rgba(76, 81, 211, 0.05) 0%, transparent 50%),
				radial-gradient(circle at 75% 75%, rgba(113, 132, 173, 0.05) 0%, transparent 50%)
			`
				: `
				radial-gradient(circle at 25% 25%, rgba(76, 81, 211, 0.03) 0%, transparent 50%),
				radial-gradient(circle at 75% 75%, rgba(113, 132, 173, 0.03) 0%, transparent 50%)
			`,
		pointerEvents: 'none',
		zIndex: -1,
	},
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		padding: 0,
	},
}))

const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme, open }) => ({
	background: 'linear-gradient(135deg, #1a1a40 0%, #2d2d5f 50%, #4C51D3 100%)',
	backdropFilter: 'blur(20px)',
	borderBottom: '1px solid rgba(76, 81, 211, 0.2)',
	boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(['margin', 'width'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		width: `calc(100% - ${drawerWidth}px)`,
		marginLeft: `${drawerWidth}px`,
		transition: theme.transitions.create(['margin', 'width'], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
	[theme.breakpoints.down('md')]: {
		zIndex: theme.zIndex.drawer - 1,
		width: '100%',
		marginLeft: 0,
	},
}))

const Drawer = styled(MuiDrawer)(({ theme }) => ({
	width: drawerWidth,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	'& .MuiDrawer-paper': {
		width: drawerWidth,
		background:
			theme.palette.mode === 'dark'
				? 'linear-gradient(180deg, #1a1a40 0%, #0f0f23 100%)'
				: 'linear-gradient(180deg, #f8f9ff 0%, #e8ebff 100%)',
		borderRight: `1px solid ${
			theme.palette.mode === 'dark'
				? 'rgba(76, 81, 211, 0.2)'
				: 'rgba(76, 81, 211, 0.1)'
		}`,
		boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
	},
	'& .MuiListItemText-root': {
		opacity: 1,
		marginLeft: '1rem',
	},
}))

const AppLayout = ({ children }: Props) => {
	const theme = useTheme()
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'))

	const navigate = useNavigate()
	const dispatch = useDispatch()

	const userInfo = useSelector(selectuserInfo)

	const themeMode = useSelector(selectDisplayMode)
	const gnbToggle = useSelector(selectGnbToggle)

	const envSetting = useSelector(selectEnvSettingInfo)
	const setting = getSettings()

	const [gnbUsername] = useState('U')
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
	const menuOpen = Boolean(anchorEl)
	const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget)
	}
	const handleMenuClose = () => {
		setAnchorEl(null)
	}

	// 뷰포트 변경 시 Drawer 상태 관리 (모바일 <-> 데스크탑 전환 버그 수정)
	useEffect(() => {
		if (isMdUp) {
			// 데스크탑으로 전환 시 Drawer 열기
			dispatch(setGnbToggle(true))
		} else {
			// 모바일로 전환 시 Drawer 닫기
			dispatch(setGnbToggle(false))
		}
	}, [isMdUp, dispatch])

	/**
	 * 메뉴 열기/닫기 토글
	 */
	const handleDrawerOpen = () => {
		dispatch(setGnbToggle(!gnbToggle))
	}

	/**
	 * 메뉴 닫기
	 */
	const handleDrawerClose = () => {
		//setOpen(false)
		dispatch(setGnbToggle(false))
	}

	/**
	 * 다크모드 전환
	 */
	const switchTheme = () => {
		const themeColor = document.querySelector(
			"meta[name='theme-color']"
		) as HTMLElement
		themeColor.setAttribute(
			'content',
			themeMode.isDarkMode ? '#323036' : '#4C51D3'
		)
		dispatch(setDisplayMode(!themeMode.isDarkMode))
	}

	/**
	 * 로그아웃
	 */
	const logoutAction = () => {
		console.log(userInfo)
		const deviceInfo: DeviceInfoModel = getUserDeviceInfo()
		console.log(deviceInfo)
		confirmAlert(
			'로그아웃 하시겠습니까?',
			() => {
				// EventSource를 먼저 닫아 추가 요청 방지
				if (eventSourceRef.current) {
					eventSourceRef.current.close()
					eventSourceRef.current = null
				}
				setListening(false)

				apiLogout(deviceInfo)
					.then(() => {
						removeCurrentUser()
						dispatch(removeUserInfo())
						navigate('/')
					})
					.catch((error) => {
						console.error('로그아웃 오류:', error)
						// 로그아웃 API 실패해도 로컬 상태는 정리
						removeCurrentUser()
						dispatch(removeUserInfo())
						navigate('/')
					})
			},
			() => {}
		)
	}

	const [menuList, setMenuList] = useState<any[]>([])

	useEffect(() => {
		apiGetMenuList().then((res) => {
			setMenuList(res)
		})
	}, [])

	/**
	 * 아래부터 SSE 코드
	 */
	const eventSourceRef = useRef<EventSource | null>(null)
	const [listening, setListening] = useState(false)
	const [messages, setMessages] = useState<SSEEventType | null>(null)

	const setupEventSource = () => {
		eventSourceRef.current = new EventSource(
			`${process.env.REACT_APP_HOST}/message/subscribe`
		)
		eventSourceRef.current.onopen = (event) => {
			console.log('onopen')
			console.log(event)
		}
		eventSourceRef.current.onmessage = (event) => {
			console.log('onmessage')
			if (!event.data) return
			const newMessage = JSON.parse(event.data)
			console.log(newMessage)
			setMessages(newMessage)
		}
		eventSourceRef.current.onerror = (event) => {
			console.log('onerror')
			console.log(event)
			if (
				event.currentTarget instanceof EventSource &&
				event.currentTarget.readyState === EventSource.CLOSED
			) {
				console.log('closed')
			}
			eventSourceRef.current?.close()
		}

		setListening(true)

		return () => {
			eventSourceRef.current?.close()
		}
	}

	useEffect(() => {
		if (!listening) setupEventSource()
	}, [])

	useEffect(() => {}, [messages?.timestamp])

	const [popupIsOpen, setPopupIsOpen] = useState(false)
	const popupClose = () => {
		setPopupIsOpen(false)
	}

	// React Query로 대시보드 통계 조회
	const { data: popupList } = useQuery({
		queryKey: ['popupList'],
		queryFn: async () => {
			const result = await apiPopupList()
			console.log('apiPopupList 호출 성공 - 결과:', result)
			return result
		},
		staleTime: 5 * 60 * 1000, // 5분간 fresh 상태 유지
		gcTime: 10 * 60 * 1000, // 10분간 캐시 유지

		// 자동 리페칭 옵션들
		refetchOnWindowFocus: true, // 윈도우 포커스 시 자동 리페칭 (기본값: true)
		refetchOnReconnect: true, // 네트워크 재연결 시 자동 리페칭 (기본값: true)
		refetchOnMount: true, // 컴포넌트 마운트 시 자동 리페칭 (기본값: true)

		// 주기적 리페칭 (옵션)
		refetchInterval: 5 * 60 * 1000, // 5분마다 자동 리페칭
		refetchIntervalInBackground: false, // 백그라운드에서도 리페칭 (기본값: false)
	})

	const notShowToday = () => {
		// 오늘 날짜를 체크해서 setSettings에 notShowToday: true, todayString 값 저장
		const today = new Date()
		const todayString = today.toISOString().split('T')[0]
		setSettings({
			...setting,
			notShowToday: true,
			todayString: todayString,
			popupCount: popupList.length || 0,
		})
		dispatch(
			setEnvSetting({
				...envSetting,
				notShowToday: true,
				todayString: todayString,
				popupCount: popupList.length || 0,
			})
		)
		setPopupIsOpen(false)
	}

	/**
	 * 대시보드 통계 데이터 변경 시 팝업 오픈 여부 결정
	 * - popupCount가 popupList.length보다 작으면 팝업 오픈
	 * - todayString이 다르면 팝업 오픈
	 * - notShowToday가 false이고 popupList.length가 0보다 크면 팝업 오픈
	 */
	useEffect(() => {
		if (popupList) {
			console.log(popupList)
			const today = new Date()
			const todayString = today.toISOString().split('T')[0]

			if (
				setting.popupCount < popupList.length ||
				setting.todayString !== todayString ||
				(setting.notShowToday === false && popupList.length > 0)
			) {
				setPopupIsOpen(true)
			}
		}
	}, [popupList])

	return (
		<>
			<ThemeProvider theme={themeMode.isDarkMode ? darkTheme : lightTheme}>
				<CssBaseline />
				{popupList?.length > 0 && (
					<NoticePopup
						popupIsOpen={popupIsOpen}
						popupClose={popupClose}
						popupList={popupList || []}
						notShowToday={notShowToday}
					/>
				)}
				<Box>
					<AppBar
						position="sticky"
						open={gnbToggle}
					>
						<Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<IconButton
									color="inherit"
									aria-label="open drawer"
									onClick={handleDrawerOpen}
									edge="start"
									sx={{
										color: 'rgba(255, 255, 255, 0.9)',
										'&:hover': {
											backgroundColor: 'rgba(255, 255, 255, 0.1)',
										},
									}}
								>
									<MenuIcon />
								</IconButton>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								{/* 테마 스위치 */}
								{/* <MaterialUISwitch
									checked={themeMode.isDarkMode}
									onChange={switchTheme}
								/> */}
								<Tooltip title="Account settings">
									<IconButton
										onClick={handleMenuClick}
										size="small"
										aria-controls={menuOpen ? 'account-menu' : undefined}
										aria-haspopup="true"
										aria-expanded={menuOpen ? 'true' : undefined}
										sx={{
											'&:hover': {
												backgroundColor: 'rgba(255, 255, 255, 0.1)',
											},
										}}
									>
										<Avatar
											sx={{
												width: 32,
												height: 32,
												background: 'linear-gradient(45deg, #4C51D3, #7184AD)',
												color: 'white',
												fontSize: '14px',
												fontWeight: 600,
											}}
										>
											{gnbUsername || 'U'}
										</Avatar>
									</IconButton>
								</Tooltip>
								<Menu
									anchorEl={anchorEl}
									id="account-menu"
									open={menuOpen}
									onClose={handleMenuClose}
									onClick={handleMenuClose}
									PaperProps={{
										elevation: 0,
										sx: {
											overflow: 'visible',
											filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
											mt: 1.5,
											background: themeMode.isDarkMode
												? 'rgba(26, 26, 64, 0.95)'
												: 'rgba(255, 255, 255, 0.95)',
											backdropFilter: 'blur(20px)',
											border: `1px solid ${
												themeMode.isDarkMode
													? 'rgba(76, 81, 211, 0.3)'
													: 'rgba(76, 81, 211, 0.1)'
											}`,
											borderRadius: '12px',
											'& .MuiAvatar-root': {
												width: 32,
												height: 32,
												ml: -0.5,
												mr: 1,
											},
											'&:before': {
												content: '""',
												display: 'block',
												position: 'absolute',
												top: 0,
												right: 14,
												width: 10,
												height: 10,
												bgcolor: themeMode.isDarkMode
													? 'rgba(26, 26, 64, 0.95)'
													: 'rgba(255, 255, 255, 0.95)',
												transform: 'translateY(-50%) rotate(45deg)',
												zIndex: 0,
											},
										},
									}}
									transformOrigin={{ horizontal: 'right', vertical: 'top' }}
									anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
								>
									<MenuItem
										onClick={logoutAction}
										sx={{
											borderRadius: '8px',
											mx: 1,
											'&:hover': {
												backgroundColor: themeMode.isDarkMode
													? 'rgba(76, 81, 211, 0.1)'
													: 'rgba(76, 81, 211, 0.05)',
											},
										}}
									>
										<ListItemIcon>
											<Logout
												fontSize="small"
												sx={{
													color: themeMode.isDarkMode ? '#4C51D3' : '#7184AD',
												}}
											/>
										</ListItemIcon>
										<Typography variant="inherit">로그아웃</Typography>
									</MenuItem>
								</Menu>
							</Box>
						</Toolbar>
					</AppBar>
					<Drawer
						variant={isMdUp ? 'persistent' : 'temporary'}
						anchor="left"
						open={gnbToggle}
						onClose={handleDrawerClose}
						sx={{
							'& .MuiDrawer-paper': {
								background: 'rgba(26,25,55)',
							},
						}}
					>
						<DrawerHeader
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								padding: '0',
							}}
						>
							{/* <IconButton
								onClick={handleDrawerClose}
								sx={{
									display: isMdUp ? 'none' : 'block',
									color: themeMode.isDarkMode ? '#4C51D3' : '#7184AD',
									'&:hover': {
										backgroundColor: themeMode.isDarkMode
											? 'rgba(76, 81, 211, 0.1)'
											: 'rgba(76, 81, 211, 0.05)',
									},
								}}
							>
								{theme.direction === 'rtl' ? (
									<ChevronRightIcon />
								) : (
									gnbToggle && <ChevronLeftIcon />
								)}
							</IconButton> */}
							<Typography
								variant="h6"
								sx={{ ml: 2, color: '#fff' }}
							>
								{process.env.REACT_APP_TITLE}
							</Typography>
						</DrawerHeader>
						<Gnb
							menuList={menuList}
							open={gnbToggle}
							handleDrawerClose={handleDrawerClose}
						/>
					</Drawer>
					<Main open={gnbToggle}>
						<Box
							sx={{
								pl: isMdUp && gnbToggle ? `${drawerWidth}px` : 0,
								transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
								height: '100%',
								position: 'relative',
								zIndex: 1,
							}}
						>
							{children}
						</Box>
					</Main>
				</Box>
			</ThemeProvider>
		</>
	)
}

export default AppLayout
